import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import JoditEditor from "jodit-react";
import { ProtectAPi } from '../../../../../api/ProtectAPi';
import swal from 'sweetalert';
import BackButton from '../../../../tools/BackButton';

const RdAdd = () => {
    const { http } = ProtectAPi();
    const editor = useRef(null);
    const navigate = useNavigate();

    const [loader, setLoader] = useState('off');
    const [layoutTitle, setLayoutTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [layoutDescription, setLayoutDescription] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [layoutImage, setLayoutImage] = useState(null);
    const [altText, setAltText] = useState('');

    const [imagePreview, setImagePreview] = useState('');
    const [layoutImageError, setLayoutImageError] = useState('');
    const [titleError, setTitleError] = useState('');
    const [slugError, setSlugError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [youtubeUrlError, setYoutubeUrlError] = useState('');
    const [altTextError, setAltTextError] = useState('');

    const submitForm = () => {
        setLoader('on');
        const formData = new FormData();
        formData.append('title', layoutTitle);
        formData.append('slug', slug);
        formData.append('description', layoutDescription);
        formData.append('image', layoutImage);
        formData.append('alt_text', altText);
        formData.append('url', youtubeUrl);

        http.post(`/store-rd`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        }).then(res => {
            if (res.data.status === '422') {
                const errors = res.data.error;
                setLoader('off');
                setLayoutImageError(errors.image?.[0] || '');
                setTitleError(errors.title?.[0] || '');
                setSlugError(errors.slug?.[0] || '');
                setDescriptionError(errors.description?.[0] || '');
                setYoutubeUrlError(errors.url?.[0] || '');
                setAltTextError(errors.alt_text?.[0] || '');
            } else {
                setLoader('off');
                navigate('/admin/pages/research-development');
                swal('Success', res.data.msg, 'success');
            }
        }).catch(error => {
            setLoader('off');
            console.error('Error:', error);
        });
    };

    const handleImageChange = (e) => {
        const selectedImage = e.target.files[0];
        setLayoutImage(selectedImage);
        const previewURL = URL.createObjectURL(selectedImage);
        setImagePreview(previewURL);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'layoutTitle') {
            setLayoutTitle(value);
        } else if (name === 'slug') {
            setSlug(value);
        } else if (name === 'youtubeUrl') {
            setYoutubeUrl(value);
        } else if (name === 'altText') {
            setAltText(value);
        }
    };

    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Start typing...',
        uploader: {
            insertImageAsBase64URI: true
        }
    }), []);

    return (
        <div className="main-content">
            <section className="section">
                <div className="section-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="card card-primary">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <h5>Add Research & Development</h5>
                                        <BackButton />
                                    </div>

                                    <div className="row mt-3">
                                        {/* Image Upload with Alt Text */}
                                        <div className="col-md-4">
                                            <label>Research Image</label>
                                            <div className="logo-box">
                                                <label htmlFor="layoutImage">
                                                    <div className="image-box border dottet p-2 rounded">
                                                        {imagePreview ? (
                                                            <img src={imagePreview} className="img-fluid" alt="Preview" />
                                                        ) : (
                                                            <img src="https://w7.pngwing.com/pngs/159/705/png-transparent-computer-computer-trademark-computer-logo.png" className="img-fluid" alt="Default" />
                                                        )}
                                                    </div>
                                                    <input type="file" id="layoutImage" className="form-control d-none" onChange={handleImageChange} />
                                                    <span className="btn btn-primary mt-1 w-100">Upload Research Image</span>
                                                    <span className="text-danger">{layoutImageError}</span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Title */}
                                        <div className="col-md-12 mt-3">
                                            <div className="form-group">
                                                <label>Image Alt Text </label>
                                                <input
                                                    type="text"
                                                    name="altText"
                                                    placeholder="Enter alt text for the image"
                                                    className="form-control mt-2"
                                                    value={altText}
                                                    onChange={handleInputChange}
                                                />
                                                 <span className="text-danger">{altTextError}</span>
                                            </div>
                                        </div>

                                        {/* Title */}
                                        <div className="col-md-12 mt-3">
                                            <div className="form-group">
                                                <label>Research Title</label>
                                                <input
                                                    type="text"
                                                    name="layoutTitle"
                                                    value={layoutTitle}
                                                    className="form-control"
                                                    placeholder="Enter research title"
                                                    onChange={handleInputChange}
                                                />
                                                <span className="text-danger">{titleError}</span>
                                            </div>
                                        </div>

                                        {/* Slug */}
                                        <div className="col-md-12 mt-2">
                                            <div className="form-group">
                                                <label>Slug (Optional)</label>
                                                <input
                                                    type="text"
                                                    name="slug"
                                                    value={slug}
                                                    className="form-control"
                                                    placeholder="Enter SEO-friendly slug (optional)"
                                                    onChange={handleInputChange}
                                                />
                                                <span className="text-danger">{slugError}</span>
                                            </div>
                                        </div>

                                        {/* YouTube URL */}
                                        <div className="col-md-12 mt-2">
                                            <div className="form-group">
                                                <label>YouTube Video URL</label>
                                                <input
                                                    type="url"
                                                    name="youtubeUrl"
                                                    value={youtubeUrl}
                                                    className="form-control"
                                                    placeholder="Paste YouTube video URL"
                                                    onChange={handleInputChange}
                                                />
                                                <span className="text-danger">{youtubeUrlError}</span>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="col-md-12 mt-4">
                                            <label>Research Description</label>
                                            <JoditEditor
                                                ref={editor}
                                                value={layoutDescription}
                                                config={{
                                                    readonly: false,
                                                    height: 400,
                                                    placeholder: 'Start typing...',
                                                    uploader: {
                                                        insertImageAsBase64URI: true
                                                    }
                                                }}
                                                tabIndex={1}
                                                onBlur={newContent => setLayoutDescription(newContent)}
                                                onChange={() => { }}
                                            />
                                            <span className="text-danger">{descriptionError}</span>
                                        </div>


                                        <div className=" col-md-12 mt-5 mb-4">
                                            {loader === 'off' ? (
                                                <button onClick={submitForm} className="btn btn-lg btn-primary">Create</button>
                                            ) : (
                                                <div className="spinner-border text-dark" role="status"></div>
                                            )}
                                        </div>


                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RdAdd;
